import { AxiosResponse } from "axios";
import ApiService from "./apiService";
import { BEConfig } from "../config/env";
import {
  webapiGet,
  webapiDelete,
  webapiPost,
  webapiPut
} from "../webapis/core";
import { ContextStorage } from "@/libs/contextStorage";
import { selectCurrency } from "../util/selectors";
import { fetchCartData, saveToCart } from "../redux/actions/cart.action";

import { Product } from "./productService";
import { DELIVERY_TYPES } from "@/constants";

class CartService extends ApiService {
  async fetchCartData() {
    const response = (await webapiGet(
      this.getAccessToken(),
      this.getBaseUrl(),
      this.getVisitorId(),
      this.getLanguage()
    ).request) as AxiosResponse<{ errors: any; products: Cart; messages: any }>;

    return response.data;
  }

  async deleteCartData(productId, id) {
    const endpoint = `${this.getBaseUrl()}/${productId}/${id}`;

    const response = await webapiDelete(
      this.getAccessToken(),
      endpoint,
      this.getVisitorId()
    ).request;

    return response;
  }

  async repopulateCart({ productData, countryId }) {
    const endpoint = `${this.getBaseUrl()}/repopulate`;

    const response = await webapiPost(
      this.getAccessToken(),
      endpoint,
      { productData, countryId },
      this.getVisitorId()
    ).request;

    return response.data;
  }

  getBaseUrl() {
    const { baseURL, protocol, port, cartPageHandle, versionInfo } =
      BEConfig.wishListApi;

    const url = `${protocol}${baseURL}${port}${versionInfo}${cartPageHandle}`;

    return url;
  }

  getCurrency() {
    const state = ContextStorage.store.getState();
    return selectCurrency(state);
  }

  changeCartItemQuantity(quantity, item) {
    const body = { quantity, ...item };

    ContextStorage.store
      .dispatch(saveToCart(body, false))
      .then(resp => {
        if (resp && resp.data.success) {
          ContextStorage.store.dispatch(fetchCartData(true, false));
        }
      })
      .catch(console.error);
  }

  async getCartAndWishlistGeneralData() {
    const { baseURL, protocol, port, versionInfo } = BEConfig.wishListApi;

    const endpoint = `${protocol}${baseURL}${port}${versionInfo}count`;

    const response = await webapiGet(
      this.getAccessToken(),
      endpoint,
      this.getVisitorId()
    ).request;

    return response;
  }

  async getCartSummary(
    body: OrderCheckoutSummaryRequestBody
  ): Promise<OrderSummaryResponse> {
    const { baseURL, protocol, port, checkoutHandle, versionInfo } =
      BEConfig.checkoutApi;

    const endpoint = `${protocol}${baseURL}${port}${versionInfo}${checkoutHandle}/summary`;

    const response = await webapiPost(
      this.getAccessToken(),
      endpoint,
      body,
      this.getVisitorId()
    ).request;

    return response.data;
  }

  validateCheckout = async address => {
    const { baseURL, protocol, port, checkoutHandle, versionInfo } =
      BEConfig.checkoutApi;
    const endpoint = `${protocol}${baseURL}${port}${versionInfo}${checkoutHandle}/validate-checkout`;
    const response = await webapiPost(
      this.getAccessToken(),
      endpoint,
      { ...(address && { address }) },
      this.getVisitorId()
    ).request;

    return response.data;
  };

  updateItemsSelection = async (itemId, isSelected) => {
    const {
      baseURL,
      protocol,
      port,
      versionInfo,
      cartPageHandle,
      updateItemSelection
    } = BEConfig.wishListApi;
    const endpoint = `${protocol}${baseURL}${port}${versionInfo}${cartPageHandle}/${updateItemSelection}`;

    const body = {
      cartIds: itemId,
      isSelected
    };
    const response = await webapiPut(
      this.getAccessToken(),
      endpoint,
      body,
      this.getVisitorId()
    ).request;

    return response;
  };

  async removeSelectedItemsFromCart(itemIds) {
    const {
      baseURL,
      protocol,
      port,
      versionInfo,
      cartPageHandle,
      removeSelectedItemsFromCart
    } = BEConfig.wishListApi;

    const endpoint = `${protocol}${baseURL}${port}${versionInfo}${cartPageHandle}/${removeSelectedItemsFromCart}`;

    const response = await webapiPost(
      this.getAccessToken(),
      endpoint,
      { cartIds: itemIds },
      this.getVisitorId()
    ).request;

    return response;
  }

  async moveSelectedItemsToWishList(itemIds) {
    const {
      baseURL,
      protocol,
      port,
      versionInfo,
      cartPageHandle,
      moveSelectedItemsToWishList
    } = BEConfig.wishListApi;

    const endpoint = `${protocol}${baseURL}${port}${versionInfo}${cartPageHandle}/${moveSelectedItemsToWishList}`;

    const response = await webapiPost(
      this.getAccessToken(),
      endpoint,
      { cartIds: itemIds },
      this.getVisitorId()
    ).request;

    return response;
  }
}

export type CartProduct = Product & {
  _id: string;
  productId: number;
  quantity: number;
  size: string;
  sizeId: number;
  sizeType: string;
  visitorId?: string;
  errors: any;
  url?: string;
  upc: string;
};

export type Cart = CartProduct[];

export type OrderSummaryRequestBody = {
  deliveryType: string;
};

export type OrderCheckoutSummaryRequestBody = {
  stateId?: number;
};

export type PriceDetails = {
  currency: string;
  discountAmount: number;
  discountAmountLabel: string;
  shippingCharge: number;
  shippingChargeLabel: string;
  subTotal: number;
  subTotalLabel: string;
  totalPrice: number;
  totalPriceLabel: string;
  walletAmount: number;
  walletAmountLabel: string;
  globalShippingCharge?: number;
  beforeDiscount?: {
    globalShippingCharge?: number;
    globalShippingChargeLabel?: string;
    shippingCharge?: number;
    shippingChargeLabel?: string;
    subTotal: number;
    subTotalLabel: string;
    totalPrice: number;
    totalPriceLabel: string;
  };
  information?: {
    globalShippingCharge: {
      messages: any[];
    };
    rrp: {
      messages: any[];
    };
    savedAmount: {
      messages: any[];
    };
    shippingCharge: {
      messages: any[];
    };
  };
};

export type OrderSummary = {
  deliveryDetails: OrderSummaryDeliveryDetails[];
  deliveryTypes?: OrderSummaryDeliveryDetails[];
  priceDetails: PriceDetails;
  states?: any;
};

export type OrderSummaryResponse = {
  deliveryTypes: OrderSummaryDeliveryDetails[];
  priceDetails: PriceDetails;
  states?: any;
};

export type OrderSummaryDeliveryDetails = {
  deliveryType: DeliveryType;
  paymentTypes: PaymentType[];
  orderAmount: number;
  messages: string[];
  errors: string[];
  freeShippingMessage: string;
  title: string;
  shippingCharge: number;
  freeShippingLimit: number;
  isDisabled?: boolean;
  deliveryOptions?: any[];
};

export type DeliveryType = {
  key: DELIVERY_TYPES;
  label: string;
  defaultValue: string;
};

export type PaymentType = {
  paymentType: string;
  currencyCode: string;
  paymentGateway: string;
  tier: string;
  sortOrder: number;
  title: string;
  description: string;
  detailedDescription?: string;
};

const instance = new CartService();

export default instance;
